<script setup>
// QR intro body: stylized viewfinder preview + "Open scanner" CTA. QR always
// starts on the normal sheet so users get the same context as other methods
// before the full-screen takeover.
// Port of `QrIntroBody` in the source `_shared/CheckInSheet.tsx`.
import { computed } from 'vue'
import { ScanLine, Camera } from '@lucide/vue'
import { computeCheckInAvailability } from './checkin'
import BlockedNotice from './BlockedNotice.vue'

const props = defineProps({
  spec: { type: Object, required: true },
})

defineEmits(['open-scanner'])

const availability = computed(() => computeCheckInAvailability(props.spec.details))
const blocked = computed(() => !availability.value.ok)
</script>

<template>
  <div class="cs-qr-intro">
    <!-- Stylized viewfinder preview -->
    <div class="cs-qr-intro__finder">
      <div class="cs-qr-intro__finder-bg" />
      <div class="cs-qr-intro__corner cs-qr-intro__corner--tl" />
      <div class="cs-qr-intro__corner cs-qr-intro__corner--tr" />
      <div class="cs-qr-intro__corner cs-qr-intro__corner--bl" />
      <div class="cs-qr-intro__corner cs-qr-intro__corner--br" />
      <ScanLine class="cs-qr-intro__icon" :size="32" />
    </div>

    <div class="cs-qr-intro__instructions">
      {{ spec.instructions ?? 'Scan the QR code posted at the venue.' }}
    </div>

    <div class="cs-qr-intro__footer">
      <BlockedNotice :availability="availability" />
      <button
        id="checkin-qr-open-scanner"
        type="button"
        class="cs-qr-intro__cta gtm-click"
        :disabled="blocked"
        @click="$emit('open-scanner')"
      >
        <template v-if="blocked">
          {{ availability.nextAvailableLabel
            ? `Next check-in ${availability.nextAvailableLabel}`
            : availability.blockedLabel ?? 'Unavailable' }}
        </template>
        <span v-else class="cs-qr-intro__cta-inner">
          <Camera :size="14" /> Open scanner
        </span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `space-y-3 pt-1`
.cs-qr-intro {
  padding-top: 4px; // pt-1

  > * + * {
    margin-top: 12px; // space-y-3
  }

  // Source: `relative mx-auto grid h-36 w-36 place-items-center`
  &__finder {
    position: relative;
    margin-left: auto;
    margin-right: auto;
    display: grid;
    height: 144px; // h-36
    width: 144px; // w-36
    place-items: center;
  }

  // Source: `absolute inset-0 rounded-2xl bg-current/5`
  &__finder-bg {
    position: absolute;
    inset: 0;
    border-radius: 16px; // rounded-2xl
    background: cs-current(5%);
  }

  // Source: `absolute h-6 w-6 border-current/40` + per-corner border sides,
  // offsets `top/right/bottom/left-2`
  &__corner {
    position: absolute;
    height: 24px; // h-6
    width: 24px; // w-6
    border-color: cs-current(40%);
    border-style: solid;
    border-width: 0;

    &--tl {
      top: 8px;
      left: 8px;
      border-top-width: 2px;
      border-left-width: 2px;
    }

    &--tr {
      top: 8px;
      right: 8px;
      border-top-width: 2px;
      border-right-width: 2px;
    }

    &--bl {
      bottom: 8px;
      left: 8px;
      border-bottom-width: 2px;
      border-left-width: 2px;
    }

    &--br {
      bottom: 8px;
      right: 8px;
      border-bottom-width: 2px;
      border-right-width: 2px;
    }
  }

  // Source: `h-8 w-8 ${theme.accent}`
  &__icon {
    color: var(--fr-accent, var(--fr-primary, #FF9900));
  }

  // Source: `text-center text-[12px] ${theme.muted}`
  &__instructions {
    text-align: center;
    font-size: 12px;
    color: var(--fr-muted, #6b7280);
  }

  &__footer {
    @include cs-sticky-footer;
  }

  &__cta {
    @include cs-cta;
  }

  &__cta-inner {
    @include cs-cta-inner;
  }
}
</style>
